/**
 * CREATE TABLE COLUMNS UTILITY WITH TOGGLE ROW SUPPORT
 * 
 * Utility function to create Tabulator column definitions for asset data.
 * This function can be used by both the main AssetTable and viewer extensions
 * and includes support for expandable rows with external data integration.
 * 
 * TOGGLE ROW INTEGRATION:
 * - Conditionally adds expand column based on includeExpandColumn option
 * - Uses provided expandFormatter for expand button functionality
 * - Positions expand column as first column for consistent UX
 * - Configures expand column with proper width and alignment
 * 
 * COLUMN FEATURES:
 * - Dynamic column generation based on data structure
 * - Custom property mapping and display name resolution
 * - Date field detection and formatting
 * - Dropdown editors for status, category, and location fields
 * - Conditional header filters with override support
 * - Context menu integration for column management
 * 
 * FLEXIBILITY:
 * - Supports both main table and viewer extension contexts
 * - Configurable sorting and filtering behavior
 * - Extensible editor and formatter system
 * - Hidden column management for system fields
 */

// Custom formatter for date fields
const dateFormatter = (cell, formatterParams) => {
    const value = cell.getValue();
    if (!value) return '';

    // Check if the value is a number (Excel date serial number)
    if (typeof value === 'number' && value > 25569) { // Excel epoch starts at 1900-01-01, Unix epoch is day 25569
        // Convert Excel serial date to JavaScript Date
        const excelEpoch = new Date(1900, 0, 1);
        const jsDate = new Date(excelEpoch.getTime() + (value - 2) * 24 * 60 * 60 * 1000); // -2 to account for Excel's leap year bug
        return jsDate.toLocaleDateString();
    }

    // Check if it's already a date string or Date object
    const date = new Date(value);
    if (!isNaN(date.getTime())) {
        return date.toLocaleDateString();
    }

    // If not a valid date, return the original value
    return value;
};

// Function to detect if a field contains date values
const isDateField = (key, data) => {
    if (!data || data.length === 0) return false;

    // Check field name patterns that suggest dates
    const dateFieldPatterns = /date|time|created|updated|modified|due|start|end|expire/i;
    if (dateFieldPatterns.test(key)) return true;

    // Check if most values in this field look like dates or Excel date numbers
    const sampleSize = Math.min(10, data.length);
    let dateCount = 0;

    for (let i = 0; i < sampleSize; i++) {
        const value = data[i][key];
        if (!value) continue;

        // Check if it's an Excel date serial number (typically > 25569 for dates after 1970)
        if (typeof value === 'number' && value > 25569 && value < 100000) {
            dateCount++;
            continue;
        }

        // Check if it's a valid date string
        const date = new Date(value);
        if (!isNaN(date.getTime()) && typeof value === 'string' && value.length > 8) {
            dateCount++;
        }
    }

    // If more than 70% of sampled values look like dates, treat as date field
    return (dateCount / sampleSize) > 0.7;
};

/**
 * Create Tabulator column definitions for asset data
 * @param {Array} data - The asset data array
 * @param {Object} customPropsMap - Map of custom property field names to display names
 * @param {Object} statusOptions - Map of status IDs to display names
 * @param locationsMap
 * @param {Object} categoryOptions - Map of category IDs to display names
 * @param {Object} options - Configuration options
 * @param {boolean} options.includeExpandColumn - Whether to include expand column
 * @param {Function} options.expandFormatter - Custom formatter for expand column
 * @param {Array} options.headerContextMenu - Context menu for headers
 * @param {number} options.editableRowIndex - Index of editable row (null if none)
 * @param {boolean} options.disableSorting - Whether to disable sorting on all columns
 * @param {Object} options.categoryCustomAttributes - Map of category IDs to custom attributes arrays
 * @returns {Array} Array of Tabulator column definitions
 */
export const createTableColumns = (
    data,
    customPropsMap = {},
    statusOptions = {},
    locationsMap = {},
    categoryOptions = {},
    options = {}
) => {
    const {
        includeExpandColumn = false,
        expandFormatter = null,
        headerContextMenu = [],
        editableRowIndex = null,
        disableSorting = false,
        disableHeaderFilters = false,          // global switch
        headerFilterOverrides = {},            // { fieldName: false | "input" | "list" | fn }
        categoryCustomAttributes = {}          // { categoryId: [customAttributes] }
    } = options;

    const hiddenKeys = [
        'id', 'isActive', 'updatedAt', 'createdAt', 'createdBy', 'updatedBy',
        'companyId', 'customAttributes', 'version'
    ];

    const renameMap = {
        categoryId: 'Category',
        statusId: 'Status',
        clientAssetId: 'Client Asset ID',
        description: 'Description',
        locationId: 'Location',
    };

    const optionsMap = {
        categoryId: categoryOptions,
        statusId: statusOptions,
        locationId: locationsMap,
    };

    const allKeys = new Set(
        data.reduce((keys, item) => {
            Object.keys(item).forEach(k => keys.add(k));
            return keys;
        }, new Set())
    );

    const columns = [];

    /**
     * EXPAND COLUMN FOR TOGGLE ROW FUNCTIONALITY
     * 
     * Creates the expand column that contains clickable buttons for toggling
     * row expansion to show additional data from external sources.
     * 
     * COLUMN CONFIGURATION:
     * - No title (empty header for clean appearance)
     * - Fixed width of 40px to accommodate triangle buttons
     * - Center alignment for visual consistency
     * - No sorting capability (not applicable for action column)
     * - No header filter (action column doesn't need filtering)
     * - Uses provided expandFormatter for button rendering
     * 
     * POSITIONING:
     * - Added as first column for immediate visibility
     * - Consistent placement across different table contexts
     * - Easy access for users to expand rows
     */
    if (includeExpandColumn && expandFormatter) {
        const col = {
            title: '',
            field: 'expand',
            formatter: expandFormatter,
            width: 40,
            hozAlign: 'center',
            headerSort: false
        };
        // never add headerFilter keys here
        columns.push(col);
    }

    Array.from(allKeys).forEach(key => {
        const isHidden = hiddenKeys.includes(key);
        const title = renameMap[key] || customPropsMap[key] || key;

        // decide filter type/params
        let filterType = "input";
        let headerFilterParams = {};
        if (key === 'statusId' || key === 'categoryId' || key === 'locationId') {
            filterType = "list";
            // Since the data contains display names instead of IDs, we need to create
            // a mapping from display names to display names for the filter to work
            const displayValues = Object.values(optionsMap[key] || {});
            const filterValues = displayValues.reduce((acc, displayName) => {
                acc[displayName] = displayName;
                return acc;
            }, {});
            headerFilterParams = { values: filterValues };
        }

        // editors - only enable when editing is active
        let editor = false;
        let editorParams = {};
        if (editableRowIndex !== null) {
            if (key === 'statusId') {
                editor = 'list';
                editorParams = { values: statusOptions };
            } else if (key === 'categoryId') {
                editor = 'list';
                editorParams = { values: categoryOptions };
            } else if (key === 'locationId') {
                editor = 'list';
                editorParams = { values: locationsMap };
            } else if (!hiddenKeys.includes(key)) {
                editor = 'input';
            }
        }

        const shouldFormatAsDate = isDateField(key, data);
        const formatter = (cell) => {
            const value = cell.getValue();

            // STEP 1: Check row's category and add class if no custom attribute definition
            const cellElement = cell.getElement();
            if (cellElement) {
                // Skip standard fields
                const standardFields = [
                    'categoryId', 'statusId', 'locationId', 'clientAssetId', 'description',
                    'barcode', 'name', 'model', 'manufacturer', 'serialNumber', 'assetTag',
                    'purchaseDate', 'warrantyDate', 'installDate', 'cost', 'value'
                ];
                const isStandardField = standardFields.includes(key) || hiddenKeys.includes(key);

                if (!isStandardField) {
                    // STEP 1: Get the row data to access the category
                    const rowData = cell.getRow().getData();

                    // STEP 2: Get the category ID (convert display name to ID if needed)
                    let rowCategoryId = rowData.categoryId;
                    if (categoryOptions && typeof rowCategoryId === 'string') {
                        const categoryEntry = Object.entries(categoryOptions).find(([id, name]) => name === rowCategoryId);
                        if (categoryEntry) {
                            rowCategoryId = categoryEntry[0];
                        }
                    }

                    // STEP 3: Get the assigned custom attributes for this category
                    let hasCustomDefinition = false;
                    let categoryDataLoaded = false;

                    if (rowCategoryId && categoryCustomAttributes) {
                        // Check if we have category data loaded for this category
                        if (categoryCustomAttributes.hasOwnProperty(rowCategoryId)) {
                            categoryDataLoaded = true;
                            const categoryAttrs = categoryCustomAttributes[rowCategoryId] || [];
                            hasCustomDefinition = categoryAttrs.some(attr => attr && attr.name === key);
                        }
                    }

                    // STEP 4: Only add/remove class if we have the category data loaded
                    if (categoryDataLoaded) {
                        if (!hasCustomDefinition) {
                            cellElement.classList.add('no-custom-attribute');
                        } else {
                            cellElement.classList.remove('no-custom-attribute');
                        }
                    }
                    // If category data not loaded yet, don't add the class (wait for data)
                }
            }

            if (shouldFormatAsDate) {
                return dateFormatter(cell);
            }
            if (optionsMap[key] && optionsMap[key][value]) {
                return optionsMap[key][value];
            }
            return value;
        };

        const col = {
            title,
            field: key,
            visible: !isHidden,
            editor,
            editorParams,
            formatter,
            headerContextMenu,
            headerSort: !disableSorting,
            sorter: disableSorting
                ? undefined
                : (shouldFormatAsDate ? "date" : "string"),
        };

        // Conditionally add headerFilter only when enabled
        const override = headerFilterOverrides[key]; // could be false or custom
        const enableForThisCol =
            !disableHeaderFilters && override !== false;

        if (enableForThisCol) {
            col.headerFilter = override || filterType;
            if (col.headerFilter === "list") {
                col.headerFilterParams = headerFilterParams;
            }
        }

        columns.push(col);
    });

    return columns;
};

